import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  LineAgentSendAttachmentInput,
  LineAgentSendInput,
  LineBindCustomerInput,
  LineContactDetail,
  LineContactListItem,
  LineContactListResponse,
  LineContactStatus,
  LineCustomerAttachmentList,
  LineCustomerInterviewList,
  LineMessageView,
  LineRichMenuGenerateInput,
  LineRichMenuGenerateResult,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export type ContactListFilter = 'ALL' | LineContactStatus;

export function useLineContacts(status: ContactListFilter) {
  return useQuery({
    queryKey: ['line', 'contacts', status],
    queryFn: async (): Promise<LineContactListResponse> => {
      const res = await apiClient.get<ApiSuccess<LineContactListResponse>>('/line/contacts', {
        params: { status, limit: 50 },
      });
      return res.data.data;
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });
}

export function useLineContactDetail(id: number | null) {
  return useQuery({
    queryKey: ['line', 'contacts', 'detail', id],
    queryFn: async (): Promise<LineContactDetail> => {
      const res = await apiClient.get<ApiSuccess<LineContactDetail>>(`/line/contacts/${id}`);
      return res.data.data;
    },
    enabled: id != null,
    refetchInterval: 4000,
    refetchIntervalInBackground: false,
  });
}

export function useTakeoverContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<LineContactDetail> => {
      const res = await apiClient.post<ApiSuccess<LineContactDetail>>(
        `/line/contacts/${id}/takeover`,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['line', 'contacts', 'detail', data.id], data);
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useReleaseContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<LineContactDetail> => {
      const res = await apiClient.post<ApiSuccess<LineContactDetail>>(
        `/line/contacts/${id}/release`,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['line', 'contacts', 'detail', data.id], data);
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useCloseContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<LineContactDetail> => {
      const res = await apiClient.post<ApiSuccess<LineContactDetail>>(
        `/line/contacts/${id}/close`,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['line', 'contacts', 'detail', data.id], data);
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.post(`/line/contacts/${id}/read`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useSendAgentMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { contactId: number; text: string }): Promise<LineMessageView> => {
      const body: LineAgentSendInput = { text: params.text };
      const res = await apiClient.post<ApiSuccess<LineMessageView>>(
        `/line/contacts/${params.contactId}/messages`,
        body,
      );
      return res.data.data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['line', 'contacts', 'detail', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

// ── REQ-20260518002:綁定客戶 + 附加客戶附件 ─────────────────────────

export function useBindCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { contactId: number; customerId: number }): Promise<LineContactDetail> => {
      const body: LineBindCustomerInput = { customerId: params.customerId };
      const res = await apiClient.post<ApiSuccess<LineContactDetail>>(
        `/line/contacts/${params.contactId}/bind`,
        body,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['line', 'contacts', 'detail', data.id], data);
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useUnbindCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number): Promise<LineContactDetail> => {
      const res = await apiClient.post<ApiSuccess<LineContactDetail>>(
        `/line/contacts/${contactId}/unbind`,
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['line', 'contacts', 'detail', data.id], data);
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

export function useCustomerAttachments(contactId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['line', 'contacts', 'customer-attachments', contactId],
    queryFn: async (): Promise<LineCustomerAttachmentList> => {
      const res = await apiClient.get<ApiSuccess<LineCustomerAttachmentList>>(
        `/line/contacts/${contactId}/customer-attachments`,
      );
      return res.data.data;
    },
    enabled: enabled && contactId != null,
  });
}

/** REQ-20260519003:抓綁定客戶的所有訪談(含每筆訪談的附件) */
export function useCustomerInterviews(contactId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['line', 'contacts', 'customer-interviews', contactId],
    queryFn: async (): Promise<LineCustomerInterviewList> => {
      const res = await apiClient.get<ApiSuccess<LineCustomerInterviewList>>(
        `/line/contacts/${contactId}/customer-interviews`,
      );
      return res.data.data;
    },
    enabled: enabled && contactId != null,
  });
}

/**
 * 取 LINE 附件 presigned URL(API 回 JSON,前端再塞給 <img src>)。
 * 直接 <img src="/api/v1/line/attachments/uuid"> 不行,因為 <img> 不會帶
 * Authorization header,會被 JWT guard 擋下回 401。
 */
export async function fetchLineAttachmentUrl(uuid: string): Promise<string> {
  const res = await apiClient.get<ApiSuccess<{ url: string; mimeType: string; originalName: string }>>(
    `/line/attachments/${uuid}`,
  );
  return res.data.data.url;
}

export function useSendAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      contactId: number;
      attachmentUuid: string;
    }): Promise<LineMessageView> => {
      const body: LineAgentSendAttachmentInput = { attachmentUuid: params.attachmentUuid };
      const res = await apiClient.post<ApiSuccess<LineMessageView>>(
        `/line/contacts/${params.contactId}/messages/attachment`,
        body,
      );
      return res.data.data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['line', 'contacts', 'detail', vars.contactId] });
      void qc.invalidateQueries({ queryKey: ['line', 'contacts'] });
    },
  });
}

// ── REQ-20260519001:用 OpenAI 生 Rich Menu 圖片 ───────────────

export function useGenerateRichMenu() {
  return useMutation({
    mutationFn: async (input: LineRichMenuGenerateInput): Promise<LineRichMenuGenerateResult> => {
      const res = await apiClient.post<ApiSuccess<LineRichMenuGenerateResult>>(
        '/line/richmenu/generate',
        input,
        // 生圖可能 1~3 分鐘,把這支拉到 200s 避免 axios 自己 timeout
        { timeout: 200_000 },
      );
      return res.data.data;
    },
  });
}

/** 取某客戶綁定的所有 LINE 聯絡人(給客戶頁的「LINE 綁定」分頁) */
export function useLineContactsByCustomer(customerId: number | undefined) {
  return useQuery({
    queryKey: ['line', 'by-customer', customerId],
    queryFn: async (): Promise<LineContactListItem[]> => {
      const res = await apiClient.get<ApiSuccess<{ items: LineContactListItem[] }>>(
        `/line/contacts/by-customer/${customerId}`,
      );
      return res.data.data.items;
    },
    enabled: customerId != null,
    staleTime: 10_000,
  });
}

/** 簡易客戶搜尋(供 BindCustomerModal 使用) */
export interface CustomerSearchResult {
  id: number;
  cCode: string | null;
  simpleName: string;
  fullName: string;
}
export function useCustomerSearch(keyword: string, enabled: boolean) {
  return useQuery({
    queryKey: ['line', 'customer-search', keyword],
    queryFn: async (): Promise<CustomerSearchResult[]> => {
      const res = await apiClient.get<{ data: CustomerSearchResult[] }>('/customers', {
        params: { q: keyword, pageSize: 20, page: 1 },
      });
      const arr = res.data.data ?? [];
      return arr.map((c) => ({
        id: c.id,
        cCode: c.cCode,
        simpleName: c.simpleName,
        fullName: c.fullName,
      }));
    },
    enabled: enabled && keyword.trim().length > 0,
    staleTime: 5_000,
  });
}
